import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Drawer, Typography, Button, TextField, IconButton, Grid, MenuItem,
  CircularProgress, Alert, Divider, InputAdornment, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tooltip,
} from '@mui/material';
import {
  Close, Receipt, Save, Edit, Delete, InboxOutlined, RestartAlt,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  incomeTypesURL, weeklyCashAddURL, weeklyCashUpdateURL,
  weeklyCashURL, weeklyCashDeleteURL,
} from '../BaseURL';

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('church_token')}` });
const swalTop = { customClass: { container: 'swal-on-top' } };
const fmt = (v) => Number(v || 0).toLocaleString('en-GH', { minimumFractionDigits: 2 });
const shortDate = (d) => new Date(d).toLocaleDateString('en-GH', { day: '2-digit', month: 'short' });

const emptyForm = (dateDefault) => ({
  id: null,
  income_date: dateDefault,
  income_type_id: '', income_type_name: '', income_category: '',
  narration: '', amount: '',
  receipt_no: '',
});

export default function RecordIncomeDrawer({
  open, onClose, monthYear, periodFirstDay, periodLastDay, onSaved,
  initialEntry = null,
}) {
  const [types, setTypes]       = useState([]);
  const [entries, setEntries]   = useState([]);
  const [form, setForm]         = useState(emptyForm(periodFirstDay));
  const [saving, setSaving]     = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const isEditing = form.id != null;

  const loadEntries = useCallback(() => {
    setLoading(true);
    axios.get(`${weeklyCashURL}?month_year=${monthYear}`, { headers: headers() })
      .then(r => setEntries(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [monthYear]);

  useEffect(() => {
    if (!open) return;
    setError('');
    if (initialEntry) {
      setForm({
        id: initialEntry.id,
        income_date: initialEntry.income_date?.slice(0, 10) || periodFirstDay,
        income_type_id: initialEntry.income_type_id || '',
        income_type_name: initialEntry.income_type_name || '',
        income_category: initialEntry.income_category || '',
        narration: initialEntry.narration || '',
        amount: String(initialEntry.amount ?? ''),
        receipt_no: initialEntry.receipt_no || '',
      });
    } else {
      setForm(emptyForm(periodFirstDay));
    }
    axios.get(incomeTypesURL, { headers: headers() })
      .then(r => { if (r.data.success) setTypes(r.data.data); })
      .catch(() => {});
    loadEntries();
  }, [open, periodFirstDay, loadEntries, initialEntry]);

  const selectType = (id) => {
    const t = types.find(x => x.id === Number(id));
    if (!t) return;
    setForm(f => ({
      ...f,
      income_type_id: t.id,
      income_type_name: t.name,
      income_category: t.category || '',
      narration: f.narration || t.narration || '',
    }));
  };

  const validate = () => {
    if (!form.income_date) return 'Income date is required.';
    if (periodFirstDay && (form.income_date < periodFirstDay || form.income_date > periodLastDay)) {
      return `Date must be within ${monthYear} (${periodFirstDay} → ${periodLastDay}).`;
    }
    if (!form.income_type_id) return 'Select an income type.';
    if (!form.amount || isNaN(form.amount) || Number(form.amount) <= 0) return 'Enter a valid amount.';
    return null;
  };

  const resetForm = (carryReceipt = '') => {
    setForm({ ...emptyForm(periodFirstDay), receipt_no: carryReceipt });
    setError('');
  };

  // Increment the trailing digits of a receipt number, preserving any prefix and width.
  //   "111"     → "112"
  //   "001"     → "002"
  //   "REC-105" → "REC-106"
  //   ""        → ""      (blank stays blank)
  //   "ABC"     → ""      (no trailing digits — clear it)
  const bumpReceipt = (r) => {
    if (!r) return '';
    const m = String(r).match(/^(.*?)(\d+)$/);
    if (!m) return '';
    const [, prefix, num] = m;
    const next = String(parseInt(num, 10) + 1).padStart(num.length, '0');
    return prefix + next;
  };

  const save = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true); setError('');
    const payload = { ...form, payment_method: 'cash', cheque_no: null };
    try {
      if (isEditing) {
        await axios.post(weeklyCashUpdateURL, payload, { headers: headers() });
      } else {
        await axios.post(weeklyCashAddURL, payload, { headers: headers() });
      }
      Swal.fire({
        ...swalTop, icon: 'success',
        title: isEditing ? 'Income updated' : 'Income saved',
        text: `GH₵ ${fmt(form.amount)}`,
        timer: 1200, showConfirmButton: false,
      });
      resetForm(bumpReceipt(form.receipt_no));
      loadEntries();
      onSaved?.();
    } catch (e) {
      setError(e.response?.data?.message || 'Could not save entry.');
    } finally {
      setSaving(false);
    }
  };

  const editEntry = (r) => {
    setError('');
    setForm({
      id: r.id,
      income_date: r.income_date?.slice(0, 10) || periodFirstDay,
      income_type_id: r.income_type_id || '',
      income_type_name: r.income_type_name || '',
      income_category: r.income_category || '',
      narration: r.narration || '',
      amount: String(r.amount ?? ''),
      receipt_no: r.receipt_no || '',
    });
    // Scroll form into view
    document.querySelector('#income-drawer-body')?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteEntry = (r) => {
    Swal.fire({
      ...swalTop, title: 'Delete this entry?',
      text: `${r.income_type_name} — GH₵ ${fmt(r.amount)}`,
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#B91C1C', confirmButtonText: 'Delete',
    }).then(async res => {
      if (!res.isConfirmed) return;
      await axios.post(weeklyCashDeleteURL, { id: r.id }, { headers: headers() });
      // If we were editing this row, clear the form
      if (form.id === r.id) resetForm();
      loadEntries();
      onSaved?.();
    });
  };

  const monthTotal = entries.reduce((s, e) => s + Number(e.amount || 0), 0);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, borderRadius: 0 } }}>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 2,
          background: 'linear-gradient(90deg, #0B2545, #13315C)', color: '#fff', px: 3, py: 2.5,
          flexShrink: 0,
        }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 1.5, bgcolor: 'secondary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(200,146,17,0.35)',
          }}>
            <Receipt />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {isEditing ? 'Edit Income Entry' : 'Record Income'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              {monthYear} · {entries.length} entr{entries.length === 1 ? 'y' : 'ies'} · GH₵ {fmt(monthTotal)}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#fff' }}><Close /></IconButton>
        </Box>

        {/* Scrollable body — form on top, entries table below */}
        <Box id="income-drawer-body" sx={{ flex: 1, overflowY: 'auto' }}>
          {/* ── Form ── */}
          <Box sx={{ px: 3, pt: 3, pb: 2 }}>
            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
              <Typography variant="overline" fontWeight={700} color="primary.main" sx={{ letterSpacing: '0.14em' }}>
                {isEditing ? 'Editing entry' : 'New Entry'}
              </Typography>
              {isEditing && (
                <Button size="small" startIcon={<RestartAlt />} onClick={() => resetForm(bumpReceipt(form.receipt_no))}>
                  New entry
                </Button>
              )}
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth size="small" label="Date" type="date" required
                  value={form.income_date}
                  inputProps={{ min: periodFirstDay, max: periodLastDay }}
                  onChange={e => setForm({ ...form, income_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth size="small" label="Amount" type="number" required
                  value={form.amount}
                  onChange={e => setForm({ ...form, amount: e.target.value })}
                  inputProps={{ min: 0, step: '0.01' }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">GH₵</InputAdornment>,
                    sx: { fontVariantNumeric: 'tabular-nums', fontWeight: 700 },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  select fullWidth size="small" label="Income Type" required
                  value={form.income_type_id}
                  onChange={e => selectType(e.target.value)}
                  helperText={form.income_category ? `Category: ${form.income_category}` : ' '}
                >
                  <MenuItem value=""><em>— Select income type —</em></MenuItem>
                  {types.map(t => (
                    <MenuItem key={t.id} value={t.id}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{t.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{t.category}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth size="small" label="Narration"
                  placeholder="Short note about this income"
                  value={form.narration}
                  onChange={e => setForm({ ...form, narration: e.target.value })}
                  multiline minRows={2}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth size="small" label="Receipt No"
                  placeholder="Optional"
                  value={form.receipt_no}
                  onChange={e => setForm({ ...form, receipt_no: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth size="small" label="Payment Method"
                  value="Cash" disabled
                />
              </Grid>
            </Grid>
          </Box>

          {/* ── This month's entries ── */}
          <Box sx={{
            px: 2, py: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            bgcolor: '#F8FAFC',
            borderTop: '1px solid #E2E8F0',
            borderBottom: '1px solid #E2E8F0',
          }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: 10 }}>
              This Month's Entries
            </Typography>
            <Typography variant="caption" fontWeight={700} color="text.disabled"
              sx={{ fontSize: 10, fontVariantNumeric: 'tabular-nums' }}>
              {entries.length}
            </Typography>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress size={22} /></Box>
          ) : entries.length === 0 ? (
            <Box sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              px: 3, py: 4, color: 'text.secondary', textAlign: 'center',
            }}>
              <InboxOutlined sx={{ fontSize: 36, color: '#CBD5E1', mb: 1 }} />
              <Typography variant="body2" fontWeight={600}>No entries yet for {monthYear}</Typography>
              <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5 }}>
                Fill the form above and click Save.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={70}>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Amount (GH₵)</TableCell>
                    <TableCell align="right" width={80}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map(r => {
                    const active = form.id === r.id;
                    return (
                      <TableRow key={r.id} hover selected={active}
                        sx={{
                          bgcolor: active ? 'rgba(200,146,17,0.08) !important' : undefined,
                          '& td:first-of-type': {
                            borderLeft: active ? '3px solid' : '3px solid transparent',
                            borderColor: active ? 'secondary.main' : 'transparent',
                          },
                        }}>
                        <TableCell sx={{ color: 'text.secondary', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                          {shortDate(r.income_date)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} noWrap>{r.income_type_name}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {r.income_category || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{
                          fontWeight: 700, color: 'success.main',
                          fontVariantNumeric: 'tabular-nums',
                        }}>
                          {fmt(r.amount)}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => editEntry(r)} sx={{ p: 0.3 }}>
                              <Edit sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => deleteEntry(r)} sx={{ p: 0.3 }}>
                              <Delete sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow sx={{ bgcolor: '#F1F5F9' }}>
                    <TableCell colSpan={2} sx={{ fontWeight: 800 }}>Total</TableCell>
                    <TableCell align="right" sx={{
                      fontWeight: 800, color: 'success.main',
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {fmt(monthTotal)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Footer */}
        <Divider />
        <Stack direction="row" spacing={1.5} sx={{ px: 3, py: 2, bgcolor: '#FAFBFC', flexShrink: 0 }} justifyContent="flex-end">
          <Button onClick={onClose}>Close</Button>
          <Button
            variant="contained" onClick={save} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            sx={{ px: 3 }}
          >
            {isEditing ? 'Save Changes' : 'Save Income'}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}
